import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { SNACKBAR_HOST } from '../snackbar.model';

@Component({
  selector: 'app-snackbar-stack',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
<div class="snackbar-stack" aria-live="polite" aria-relevant="additions">
  @for (snackbar of host.items(); track snackbar.id) {
    <div class="snackbar-slot" [class.is-leaving]="snackbar.leaving">
      <div class="snackbar-slot-inner">
        <div
          class="snackbar"
          [attr.data-type]="snackbar.type"
          [class.is-paused]="snackbar.paused"
          [attr.role]="snackbar.type === 'failure' ? 'alert' : 'status'"
          (mouseenter)="host.pauseTimer(snackbar.id)"
          (mouseleave)="host.resumeTimer(snackbar.id)">

          <span class="snackbar-message">{{ snackbar.message }}</span>

          @if (snackbar.action) {
            <button
              type="button"
              class="snackbar-action"
              (click)="host.dismiss(snackbar.id)">
              {{ snackbar.action }}
            </button>
          }

          @if (snackbar.duration > 0) {
            <span class="snackbar-progress" aria-hidden="true">
              <span
                class="snackbar-progress-fill"
                [class.is-odd-cycle]="snackbar.cycle % 2 === 1"
                [style.animation-duration.ms]="snackbar.duration">
              </span>
            </span>
          }
        </div>
      </div>
    </div>
  }
</div>
  `,
  styles: `
:host {
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1100;
  display: block;
  width: min(100%, 480px);
  padding: 0 8px 16px 8px;
  pointer-events: none;
}

.snackbar-stack {
  display: flex;
  flex-direction: column;
}

.snackbar-slot {
  display: grid;
  grid-template-rows: 1fr;
  transition: grid-template-rows 200ms ease, opacity 200ms ease;
  animation: snackbar-slot-enter 260ms cubic-bezier(0.2, 0, 0, 1);
}

.snackbar-slot.is-leaving {
  grid-template-rows: 0fr;
  opacity: 0;
}

.snackbar-slot-inner {
  min-height: 0;
  overflow: hidden;
}

.snackbar {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
  padding: 13px 14px 16px 16px;
  border-radius: 8px;
  overflow: hidden;
  background: #101726;
  color: #E8EDF7;
  font-size: 14px;
  line-height: 20px;
  text-align: left;
  box-shadow: 0 8px 22px rgba(8, 12, 22, .34);
  pointer-events: auto;
  animation: snackbar-enter 260ms cubic-bezier(0.2, 0, 0, 1);
}

.snackbar[data-type="success"] {
  --snackbar-accent: var(--success-color);
}

.snackbar[data-type="failure"] {
  --snackbar-accent: var(--error-color);
}

.snackbar[data-type="default"] {
  --snackbar-accent: var(--secondary-color);
}

.snackbar-message {
  flex: 1;
  overflow-wrap: anywhere;
}

.snackbar-action {
  flex: 0 0 auto;
  border: 0;
  border-radius: 4px;
  padding: 6px 12px;
  background-color: transparent;
  color: var(--snackbar-accent);
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .06em;
  text-transform: uppercase;
  cursor: pointer;
}

.snackbar-action:hover {
  background-color: rgba(255, 255, 255, .08);
}

.snackbar-action:focus-visible {
  outline: 2px solid var(--snackbar-accent);
  outline-offset: 2px;
}

.snackbar-progress {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  background-color: rgba(255, 255, 255, .18);
}

.snackbar-progress-fill {
  display: block;
  width: 100%;
  height: 100%;
  background-color: var(--snackbar-accent);
  animation-name: snackbar-drain-even;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}

.snackbar-progress-fill.is-odd-cycle {
  animation-name: snackbar-drain-odd;
}

.snackbar.is-paused .snackbar-progress-fill {
  animation-play-state: paused;
}

@keyframes snackbar-slot-enter {
  from {
    grid-template-rows: 0fr;
    opacity: 0;
  }

  to {
    grid-template-rows: 1fr;
    opacity: 1;
  }
}

@keyframes snackbar-enter {
  from {
    transform: translateY(12px);
  }

  to {
    transform: none;
  }
}

@keyframes snackbar-drain-even {
  from {
    width: 100%;
  }

  to {
    width: 0;
  }
}

@keyframes snackbar-drain-odd {
  from {
    width: 100%;
  }

  to {
    width: 0;
  }
}

@media (prefers-reduced-motion: reduce) {

  .snackbar-slot,
  .snackbar {
    animation: none;
    transition: none;
  }
}
  `
})
export class SnackbarStackComponent {
  protected readonly host = inject(SNACKBAR_HOST);
}
